import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import PageBreadcrumb from "../common/PageBreadcrumb/PageBreadcrumb";
import { useUserData } from "../../hooks/useUserData";
import { storeUserData, getUserData } from "../../services/apiService";
import {
  getExternalEntityAccessRows,
  getGrantedModules,
  hasGrantedModule,
  isSponsorCroSession,
  refreshSponsorCroLiveGrants,
  resolveIsfOpenBinderCategories,
} from "../../utils/sponsorCroAccess";
import { SIDEBAR_ORG_MODULES } from "../../utils/sidebarOrgModuleFilter";
import { fetchEisfStudies } from "./isf/isfDashboardService";
import { listInboundByStaffRecipient } from "../../services/emailCorrespondenceApi";
import {
  apiOk,
  buildInboxRows,
  formatMailboxDateTime,
  readIsReadFlag,
  rowId,
  rowPreview,
  rowSubject,
  sortInboxRowsNewestFirst,
  unreadInboxRows,
} from "../../utils/notificationInboxUtils";
import { DEFAULT_LANDING_ROUTE } from "../../constants/appRoutes";
import CtmsWorkspaceModal from "../common/ctms/CtmsWorkspaceModal";
import UserProfile from "./SuperAdmin/userProfile";
import apiClient from "../../services/apiClient";
import API_ENDPOINTS from "../../config/apiEndpoints";
import "./SponsorCroDashboard.scss";
import "./SuperAdmin/userProfile.scss";

const TABS = [
  { id: "studies", label: "Studies", icon: "fas fa-flask" },
  { id: "comments", label: "Comments", icon: "fas fa-comments" },
  { id: "inbox", label: "Inbox", icon: "fas fa-inbox" },
];

function grantStudyId(row) {
  return String(row?.StudyId ?? row?.studyId ?? "").trim();
}

function grantSiteId(row) {
  return String(row?.SiteId ?? row?.siteId ?? "").trim();
}

function grantCategory(row) {
  const raw = String(row?.Category ?? row?.category ?? "")
    .trim()
    .toLowerCase();
  if (raw === "epsf") return "ePSF";
  if (raw === "eisf") return "eISF";
  return raw ? raw.toUpperCase() : "eISF";
}

function grantScope(row) {
  return String(row?.ScopeType ?? row?.scopeType ?? "")
    .trim()
    .toUpperCase();
}

function grantFolderId(row) {
  return String(row?.FolderId ?? row?.folderId ?? "").trim();
}

function isGrantActive(row) {
  const active = row?.IsActive ?? row?.isActive;
  if (active === false || active === 0 || active === "0") return false;
  const until = row?.AccessUntil ?? row?.accessUntil;
  if (until == null || String(until).trim() === "") return true;
  const end = new Date(until);
  if (Number.isNaN(end.getTime())) return true;
  return end.getTime() >= Date.now();
}

function buildStudyCardsFromGrants(accessRows, studyById) {
  const byStudy = new Map();

  (accessRows || []).forEach((row) => {
    if (!isGrantActive(row)) return;
    const module = String(row?.Module ?? row?.module ?? "isf")
      .trim()
      .toLowerCase();
    if (module && module !== SIDEBAR_ORG_MODULES.ISF) return;

    const studyId = grantStudyId(row);
    if (!studyId) return;

    let card = byStudy.get(studyId.toLowerCase());
    if (!card) {
      const meta = studyById.get(studyId.toLowerCase()) || {};
      card = {
        studyId,
        studyName:
          meta.StudyName ||
          meta.studyName ||
          row?.StudyName ||
          row?.studyName ||
          studyId,
        studyNumber: meta.StudyNumber || meta.studyNumber || "",
        siteIds: new Set(),
        categories: new Set(),
        folderIds: new Set(),
        wholeStudy: false,
        accessUntil: null,
      };
      byStudy.set(studyId.toLowerCase(), card);
    }

    const siteId = grantSiteId(row);
    if (siteId) card.siteIds.add(siteId);
    if (grantScope(row) === "WHOLE_STUDY" && !siteId) {
      card.wholeStudy = true;
    }

    card.categories.add(grantCategory(row));

    const folderId = grantFolderId(row);
    if (folderId) card.folderIds.add(folderId);
    if (
      !folderId ||
      grantScope(row) === "ALL" ||
      grantScope(row) === "WHOLE_STUDY"
    ) {
      card.allFolders = true;
    }

    const until = row?.AccessUntil ?? row?.accessUntil;
    if (until) {
      const d = new Date(until);
      if (!Number.isNaN(d.getTime())) {
        if (!card.accessUntil || d.getTime() < card.accessUntil.getTime()) {
          card.accessUntil = d;
        }
      }
    }
  });

  return [...byStudy.values()]
    .map((card) => ({
      ...card,
      siteCount: card.wholeStudy ? null : card.siteIds.size,
      categoryList: ["eISF", "ePSF"].filter((c) => card.categories.has(c)),
      folderLabel: card.allFolders
        ? "All folders"
        : card.folderIds.size === 0
          ? "—"
          : card.folderIds.size === 1
            ? "1 selected folder"
            : `${card.folderIds.size} selected folders`,
    }))
    .sort((a, b) =>
      String(a.studyName).localeCompare(String(b.studyName), undefined, {
        sensitivity: "base",
      }),
    );
}

function daysUntil(date) {
  if (!date) return null;
  const ms = date.getTime() - Date.now();
  return Math.ceil(ms / (24 * 60 * 60 * 1000));
}

function normalizeTaggedComments(payload) {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.MyTaggedComments)) return payload.MyTaggedComments;
  if (Array.isArray(payload?.Table)) return payload.Table;
  if (Array.isArray(payload?.data)) return payload.data;
  return [];
}

function isTruthyFlag(value) {
  return value === true || value === 1 || value === "1" || value === "true";
}

function isHighPriority(value) {
  return String(value || "")
    .trim()
    .toLowerCase() === "high";
}

function initialsFromName(name) {
  const parts = String(name || "")
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (parts.length >= 2) {
    return `${parts[0][0] || ""}${parts[parts.length - 1][0] || ""}`.toUpperCase();
  }
  return (parts[0]?.[0] || "?").toUpperCase();
}

/**
 * Functional home for Sponsor/CRO users — shared studies, tagged comments, inbox.
 */
function SponsorCroDashboard() {
  const navigate = useNavigate();
  const { userData, refreshUserData } = useUserData();
  const [loading, setLoading] = useState(true);
  const [studyCards, setStudyCards] = useState([]);
  const [recentMail, setRecentMail] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [taggedComments, setTaggedComments] = useState([]);
  const [activeTab, setActiveTab] = useState("studies");
  const [myProfileModalOpen, setMyProfileModalOpen] = useState(false);

  const displayName = String(
    userData?.UserName ||
      [userData?.FirstName, userData?.LastName].filter(Boolean).join(" ") ||
      "",
  ).trim();
  const orgName = String(
    userData?.OrganizationName ||
      userData?.organizationName ||
      userData?.OrgName ||
      "",
  ).trim();

  const hasIsf = hasGrantedModule(userData, SIDEBAR_ORG_MODULES.ISF);
  const grantedModules = getGrantedModules(userData);

  const openCommentCount = useMemo(
    () =>
      taggedComments.filter((c) => !isTruthyFlag(c?.IsResolved ?? c?.isResolved))
        .length,
    [taggedComments],
  );

  const load = useCallback(async () => {
    const sessionUser = getUserData() || userData;
    if (!isSponsorCroSession(sessionUser)) {
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const liveUser = await refreshSponsorCroLiveGrants(sessionUser);
      if (liveUser) {
        storeUserData(liveUser);
        refreshUserData?.();
      }

      const organizationId = liveUser?.OrganizationId;
      const staffId = liveUser?.StaffId;
      const staffEmail = liveUser?.EmailId || liveUser?.emailId || "";

      const accessRows = getExternalEntityAccessRows(liveUser);

      let studyById = new Map();
      if (organizationId && staffId) {
        try {
          const { studies } = await fetchEisfStudies({
            organizationId,
            createdBy: staffId,
            force: true,
          });
          (studies || []).forEach((s) => {
            const id = String(s?.StudyId ?? "").trim().toLowerCase();
            if (id) studyById.set(id, s);
          });
        } catch (err) {
          console.warn("Sponsor/CRO dashboard study list", err);
        }
      }

      setStudyCards(buildStudyCardsFromGrants(accessRows, studyById));

      const mailPromise =
        organizationId != null
          ? listInboundByStaffRecipient({
              organizationRefId: organizationId,
              take: 40,
            }).catch((err) => {
              console.warn("Sponsor/CRO dashboard inbox", err);
              return null;
            })
          : Promise.resolve(null);

      const commentsPromise =
        staffId != null
          ? apiClient
              .post(API_ENDPOINTS.ISF.MY_TAGGED_COMMENTS, {
                StaffRecordIdRef: staffId,
                OrganizationIdRef: organizationId || null,
                Top: 50,
              })
              .catch((err) => {
                console.warn("Sponsor/CRO dashboard comments", err);
                return null;
              })
          : Promise.resolve(null);

      const [mailRes, commentsRes] = await Promise.all([
        mailPromise,
        commentsPromise,
      ]);

      if (apiOk(mailRes) && Array.isArray(mailRes.data)) {
        const inbox = buildInboxRows(mailRes.data, staffEmail);
        const sorted = sortInboxRowsNewestFirst(inbox);
        setUnreadCount(unreadInboxRows(sorted).length);
        setRecentMail(sorted.slice(0, 8));
      } else {
        setUnreadCount(0);
        setRecentMail([]);
      }

      if (commentsRes?.success === 1 || commentsRes?.success === true) {
        setTaggedComments(normalizeTaggedComments(commentsRes.data));
      } else {
        setTaggedComments([]);
      }
    } finally {
      setLoading(false);
    }
  }, [userData, refreshUserData]);

  const orgId = userData?.OrganizationId;
  const staffId = userData?.StaffId;

  useEffect(() => {
    if (!userData) return;
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgId, staffId]);

  const siteTotal = useMemo(() => {
    const ids = new Set();
    let anyWhole = false;
    studyCards.forEach((c) => {
      if (c.wholeStudy || c.siteCount == null) {
        anyWhole = true;
        return;
      }
      c.siteIds.forEach((id) => ids.add(id));
    });
    if (anyWhole && ids.size === 0) return null;
    return ids.size;
  }, [studyCards]);

  const openStudy = (card) => {
    const binder = resolveIsfOpenBinderCategories(userData, {
      StudyId: card.studyId,
    });
    const cat =
      binder.defaultCategory?.value ||
      card.categoryList[0] ||
      "eISF";
    navigate(
      `/isf?studyId=${encodeURIComponent(card.studyId)}&category=${encodeURIComponent(cat)}`,
    );
  };

  const openTaggedComment = (row) => {
    const studyId = String(row?.StudyId ?? row?.studyId ?? "").trim();
    const category =
      String(row?.TemplateCategory ?? row?.templateCategory ?? "eISF").trim() ||
      "eISF";
    const documentId = String(
      row?.DocumentIdRef ?? row?.documentIdRef ?? "",
    ).trim();
    if (!studyId) {
      navigate("/isf");
      return;
    }
    const params = new URLSearchParams({
      studyId,
      category,
      openComments: "1",
    });
    if (documentId) params.set("documentId", documentId);
    navigate(`/isf?${params.toString()}`);
  };

  const [nowLabel, setNowLabel] = useState(() =>
    new Date().toLocaleString(undefined, {
      weekday: "long",
      day: "numeric",
      month: "short",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
    }),
  );

  useEffect(() => {
    const tick = () => {
      setNowLabel(
        new Date().toLocaleString(undefined, {
          weekday: "long",
          day: "numeric",
          month: "short",
          year: "numeric",
          hour: "numeric",
          minute: "2-digit",
        }),
      );
    };
    const id = window.setInterval(tick, 30000);
    return () => window.clearInterval(id);
  }, []);

  if (!isSponsorCroSession(userData)) {
    return (
      <div className="sponsor-cro-dashboard">
        <p className="text-muted mb-0">This home is for Sponsor/CRO users.</p>
      </div>
    );
  }

  const emptyAccess = !loading && studyCards.length === 0 && !hasIsf;

  return (
    <div className="sponsor-cro-dashboard">
      <PageBreadcrumb
        items={[{ label: "Home" }]}
        actions={[
          {
            icon: "fas fa-refresh",
            label: loading ? "Refreshing…" : "Refresh",
            onClick: () => load(),
          },
        ]}
      />

      <header className="sponsor-cro-dashboard__hero">
        <div className="sponsor-cro-dashboard__hero-copy">
          <p className="sponsor-cro-dashboard__eyebrow">{nowLabel}</p>
          <h1 className="sponsor-cro-dashboard__title">
            Welcome{displayName ? `, ${displayName}` : ""}
          </h1>
          <p className="sponsor-cro-dashboard__subtitle mb-0">
            {orgName
              ? `Shared workspace for ${orgName}`
              : "Your shared study workspace"}
          </p>
        </div>
        <div className="sponsor-cro-dashboard__hero-actions">
          {hasIsf ? (
            <Link to="/isf" className="btn btn-primary btn-sm">
              Open eISF
            </Link>
          ) : null}
          <button
            type="button"
            className="btn btn-outline-secondary btn-sm"
            onClick={() => setMyProfileModalOpen(true)}
            disabled={!userData?.StaffId}
          >
            My Profile
          </button>
        </div>
      </header>

      {loading ? (
        <div className="sponsor-cro-dashboard__loading" aria-busy="true">
          <div className="sponsor-cro-dashboard__skeleton-row">
            <span />
            <span />
            <span />
            <span />
          </div>
          <p className="text-muted mb-0 mt-3">Loading your shared access…</p>
        </div>
      ) : (
        <>
          <section className="sponsor-cro-dashboard__kpis" aria-label="Summary">
            <button
              type="button"
              className="sponsor-cro-dashboard__kpi"
              onClick={() => setActiveTab("studies")}
            >
              <span className="sponsor-cro-dashboard__kpi-icon" aria-hidden="true">
                <i className="fas fa-flask" />
              </span>
              <span className="sponsor-cro-dashboard__kpi-value">
                {studyCards.length}
              </span>
              <span className="sponsor-cro-dashboard__kpi-label">
                Studies shared
              </span>
            </button>
            <div className="sponsor-cro-dashboard__kpi sponsor-cro-dashboard__kpi--static">
              <span className="sponsor-cro-dashboard__kpi-icon" aria-hidden="true">
                <i className="fas fa-hospital" />
              </span>
              <span className="sponsor-cro-dashboard__kpi-value">
                {siteTotal == null ? "—" : siteTotal}
              </span>
              <span className="sponsor-cro-dashboard__kpi-label">
                Sites in scope
              </span>
            </div>
            <button
              type="button"
              className="sponsor-cro-dashboard__kpi"
              onClick={() => setActiveTab("comments")}
            >
              <span className="sponsor-cro-dashboard__kpi-icon" aria-hidden="true">
                <i className="fas fa-comments" />
              </span>
              <span className="sponsor-cro-dashboard__kpi-value">
                {openCommentCount}
              </span>
              <span className="sponsor-cro-dashboard__kpi-label">
                Open comments
              </span>
            </button>
            <button
              type="button"
              className="sponsor-cro-dashboard__kpi"
              onClick={() => setActiveTab("inbox")}
            >
              <span className="sponsor-cro-dashboard__kpi-icon" aria-hidden="true">
                <i className="fas fa-envelope" />
              </span>
              <span className="sponsor-cro-dashboard__kpi-value">
                {unreadCount}
              </span>
              <span className="sponsor-cro-dashboard__kpi-label">
                Unread emails
              </span>
            </button>
          </section>

          <div className="sponsor-cro-dashboard__workspace">
            <div className="sponsor-cro-dashboard__main-panel">
              <div
                className="sponsor-cro-dashboard__tabs"
                role="tablist"
                aria-label="Home sections"
              >
                {TABS.map((tab) => {
                  const count =
                    tab.id === "comments"
                      ? taggedComments.length
                      : tab.id === "inbox"
                        ? unreadCount
                        : studyCards.length;
                  return (
                    <button
                      key={tab.id}
                      type="button"
                      role="tab"
                      aria-selected={activeTab === tab.id}
                      className={`sponsor-cro-dashboard__tab${
                        activeTab === tab.id ? " is-active" : ""
                      }`}
                      onClick={() => setActiveTab(tab.id)}
                    >
                      <i className={tab.icon} aria-hidden="true" />
                      <span>{tab.label}</span>
                      <span className="sponsor-cro-dashboard__tab-count">
                        {count}
                      </span>
                    </button>
                  );
                })}
              </div>

              <div className="sponsor-cro-dashboard__tab-panel" role="tabpanel">
                {activeTab === "studies" ? (
                  <>
                    <div className="sponsor-cro-dashboard__section-head">
                      <div>
                        <h2 className="sponsor-cro-dashboard__section-title">
                          My shared studies
                        </h2>
                        <p className="sponsor-cro-dashboard__section-sub mb-0">
                          Studies and folders granted to your account
                        </p>
                      </div>
                      {hasIsf ? (
                        <Link
                          to="/isf"
                          className="btn btn-sm btn-outline-primary"
                        >
                          Open eISF
                        </Link>
                      ) : null}
                    </div>

                    {emptyAccess ? (
                      <div className="sponsor-cro-dashboard__empty">
                        <div className="sponsor-cro-dashboard__empty-icon">
                          <i className="fas fa-lock" aria-hidden="true" />
                        </div>
                        <p className="mb-1 fw-semibold">
                          No modules have been shared with you.
                        </p>
                        <p className="text-muted small mb-0">
                          When your organisation grants study or folder access,
                          it will appear here.
                        </p>
                      </div>
                    ) : studyCards.length === 0 ? (
                      <div className="sponsor-cro-dashboard__empty">
                        <div className="sponsor-cro-dashboard__empty-icon">
                          <i className="fas fa-folder-open" aria-hidden="true" />
                        </div>
                        <p className="mb-0 text-muted">
                          No studies are currently shared with your account.
                        </p>
                      </div>
                    ) : (
                      <ul className="sponsor-cro-dashboard__study-list">
                        {studyCards.map((card) => {
                          const days = daysUntil(card.accessUntil);
                          const expiring =
                            days != null && days >= 0 && days <= 30;
                          return (
                            <li
                              key={card.studyId}
                              className="sponsor-cro-dashboard__study-card"
                            >
                              <div className="sponsor-cro-dashboard__study-main">
                                <div className="sponsor-cro-dashboard__study-name">
                                  {card.studyName}
                                </div>
                                {card.studyNumber ? (
                                  <div className="sponsor-cro-dashboard__study-meta">
                                    {card.studyNumber}
                                  </div>
                                ) : null}
                                <div className="sponsor-cro-dashboard__study-chips">
                                  {card.categoryList.map((cat) => (
                                    <span
                                      key={cat}
                                      className="sponsor-cro-dashboard__chip"
                                    >
                                      {cat}
                                    </span>
                                  ))}
                                  <span className="sponsor-cro-dashboard__chip sponsor-cro-dashboard__chip--muted">
                                    {card.wholeStudy || card.siteCount == null
                                      ? "All sites"
                                      : card.siteCount === 1
                                        ? "1 site"
                                        : `${card.siteCount} sites`}
                                  </span>
                                  <span className="sponsor-cro-dashboard__chip sponsor-cro-dashboard__chip--muted">
                                    {card.folderLabel}
                                  </span>
                                  {expiring ? (
                                    <span className="sponsor-cro-dashboard__chip sponsor-cro-dashboard__chip--warn">
                                      Access ends in {days} day
                                      {days === 1 ? "" : "s"}
                                    </span>
                                  ) : null}
                                </div>
                              </div>
                              <button
                                type="button"
                                className="btn btn-primary btn-sm"
                                onClick={() => openStudy(card)}
                              >
                                Open
                              </button>
                            </li>
                          );
                        })}
                      </ul>
                    )}
                  </>
                ) : null}

                {activeTab === "comments" ? (
                  <>
                    <div className="sponsor-cro-dashboard__section-head">
                      <div>
                        <h2 className="sponsor-cro-dashboard__section-title">
                          Comments for you
                        </h2>
                        <p className="sponsor-cro-dashboard__section-sub mb-0">
                          When someone tags you in an eISF comment, it appears
                          here
                        </p>
                      </div>
                    </div>

                    {taggedComments.length === 0 ? (
                      <div className="sponsor-cro-dashboard__empty">
                        <div className="sponsor-cro-dashboard__empty-icon">
                          <i className="fas fa-comment-dots" aria-hidden="true" />
                        </div>
                        <p className="mb-1 fw-semibold">No tagged comments yet</p>
                        <p className="text-muted small mb-0">
                          Mentions in shared study documents will show up in this
                          list.
                        </p>
                      </div>
                    ) : (
                      <ul className="sponsor-cro-dashboard__comment-list">
                        {taggedComments.map((row) => {
                          const commentId = String(
                            row?.CommentId ?? row?.commentId ?? "",
                          );
                          const author = String(
                            row?.CommentedBy ?? row?.commentedBy ?? "Someone",
                          ).trim();
                          const body = String(
                            row?.Comment ?? row?.comment ?? "",
                          ).trim();
                          const studyName = String(
                            row?.StudyName ?? row?.studyName ?? "",
                          ).trim();
                          const fileName = String(
                            row?.FileName ?? row?.fileName ?? "",
                          ).trim();
                          const folderName = String(
                            row?.FolderName ?? row?.folderName ?? "",
                          ).trim();
                          const commentDate = String(
                            row?.CommentDate ?? row?.commentDate ?? "",
                          ).trim();
                          const resolved = isTruthyFlag(
                            row?.IsResolved ?? row?.isResolved,
                          );
                          const high = isHighPriority(
                            row?.CommentPriority ?? row?.commentPriority,
                          );
                          return (
                            <li
                              key={commentId || `${author}-${commentDate}`}
                              className={`sponsor-cro-dashboard__comment-card${
                                resolved ? " is-resolved" : ""
                              }`}
                            >
                              <div
                                className="sponsor-cro-dashboard__comment-avatar"
                                aria-hidden="true"
                              >
                                {initialsFromName(author)}
                              </div>
                              <div className="sponsor-cro-dashboard__comment-body">
                                <div className="sponsor-cro-dashboard__comment-top">
                                  <div>
                                    <div className="sponsor-cro-dashboard__comment-author">
                                      {author}
                                      <span className="sponsor-cro-dashboard__comment-verb">
                                        {" "}
                                        tagged you
                                      </span>
                                    </div>
                                    <div className="sponsor-cro-dashboard__comment-meta">
                                      {[studyName, folderName, fileName]
                                        .filter(Boolean)
                                        .join(" · ")}
                                    </div>
                                  </div>
                                  <div className="sponsor-cro-dashboard__comment-flags">
                                    {high ? (
                                      <span className="sponsor-cro-dashboard__chip sponsor-cro-dashboard__chip--warn">
                                        High
                                      </span>
                                    ) : null}
                                    {resolved ? (
                                      <span className="sponsor-cro-dashboard__chip sponsor-cro-dashboard__chip--muted">
                                        Resolved
                                      </span>
                                    ) : (
                                      <span className="sponsor-cro-dashboard__chip">
                                        Open
                                      </span>
                                    )}
                                  </div>
                                </div>
                                <p className="sponsor-cro-dashboard__comment-text">
                                  {body || "—"}
                                </p>
                                <div className="sponsor-cro-dashboard__comment-footer">
                                  <span className="sponsor-cro-dashboard__comment-date">
                                    {commentDate || "—"}
                                  </span>
                                  <button
                                    type="button"
                                    className="btn btn-sm btn-outline-primary"
                                    onClick={() => openTaggedComment(row)}
                                  >
                                    Open in eISF
                                  </button>
                                </div>
                              </div>
                            </li>
                          );
                        })}
                      </ul>
                    )}
                  </>
                ) : null}

                {activeTab === "inbox" ? (
                  <>
                    <div className="sponsor-cro-dashboard__section-head">
                      <div>
                        <h2 className="sponsor-cro-dashboard__section-title">
                          Recent messages
                        </h2>
                        <p className="sponsor-cro-dashboard__section-sub mb-0">
                          Study access and system emails for your account
                        </p>
                      </div>
                      <Link
                        to={DEFAULT_LANDING_ROUTE}
                        className="btn btn-sm btn-link px-0"
                      >
                        View all
                      </Link>
                    </div>
                    {recentMail.length === 0 ? (
                      <div className="sponsor-cro-dashboard__empty">
                        <div className="sponsor-cro-dashboard__empty-icon">
                          <i className="fas fa-inbox" aria-hidden="true" />
                        </div>
                        <p className="mb-0 text-muted">No recent messages.</p>
                      </div>
                    ) : (
                      <ul className="sponsor-cro-dashboard__mail-list">
                        {recentMail.map((row) => {
                          const unread = !readIsReadFlag(row);
                          return (
                            <li key={rowId(row) || rowSubject(row)}>
                              <Link
                                to={DEFAULT_LANDING_ROUTE}
                                state={{
                                  selectedMessageId: rowId(row) || undefined,
                                }}
                                className={`sponsor-cro-dashboard__mail-item${
                                  unread ? " is-unread" : ""
                                }`}
                              >
                                <span className="sponsor-cro-dashboard__mail-subject">
                                  {rowSubject(row)}
                                </span>
                                <span className="sponsor-cro-dashboard__mail-preview">
                                  {rowPreview(row)}
                                </span>
                                <span className="sponsor-cro-dashboard__mail-date">
                                  {formatMailboxDateTime(
                                    row?.ReceivedAtUtc ||
                                      row?.receivedAtUtc ||
                                      row?.SentAtUtc ||
                                      row?.sentAtUtc,
                                  )}
                                </span>
                              </Link>
                            </li>
                          );
                        })}
                      </ul>
                    )}
                  </>
                ) : null}
              </div>
            </div>

            <aside className="sponsor-cro-dashboard__aside">
              <section className="sponsor-cro-dashboard__panel">
                <h2 className="sponsor-cro-dashboard__section-title mb-3">
                  Quick links
                </h2>
                <div className="sponsor-cro-dashboard__quick-links">
                  {hasIsf ? (
                    <Link to="/isf" className="sponsor-cro-dashboard__quick-link">
                      <i className="fas fa-folder-open" aria-hidden="true" />
                      eISF binder
                    </Link>
                  ) : null}
                  <button
                    type="button"
                    className="sponsor-cro-dashboard__quick-link"
                    onClick={() => setActiveTab("comments")}
                  >
                    <i className="fas fa-comments" aria-hidden="true" />
                    Tagged comments
                    {openCommentCount > 0 ? (
                      <span className="sponsor-cro-dashboard__badge">
                        {openCommentCount}
                      </span>
                    ) : null}
                  </button>
                  <Link
                    to={DEFAULT_LANDING_ROUTE}
                    className="sponsor-cro-dashboard__quick-link"
                  >
                    <i className="fas fa-inbox" aria-hidden="true" />
                    Notification Centre
                    {unreadCount > 0 ? (
                      <span className="sponsor-cro-dashboard__badge">
                        {unreadCount}
                      </span>
                    ) : null}
                  </Link>
                  {userData?.StaffId ? (
                    <button
                      type="button"
                      className="sponsor-cro-dashboard__quick-link"
                      onClick={() => setMyProfileModalOpen(true)}
                    >
                      <i className="fas fa-user" aria-hidden="true" />
                      My Profile
                    </button>
                  ) : null}
                  <Link to="/faq" className="sponsor-cro-dashboard__quick-link">
                    <i className="fas fa-circle-question" aria-hidden="true" />
                    FAQ
                  </Link>
                  <Link
                    to="/my-tickets"
                    className="sponsor-cro-dashboard__quick-link"
                  >
                    <i className="fas fa-life-ring" aria-hidden="true" />
                    Support tickets
                  </Link>
                </div>
              </section>

              <section className="sponsor-cro-dashboard__panel sponsor-cro-dashboard__panel--soft">
                <h2 className="sponsor-cro-dashboard__section-title mb-2">
                  Access snapshot
                </h2>
                <ul className="sponsor-cro-dashboard__snapshot">
                  <li>
                    <span>Modules granted</span>
                    <strong>{grantedModules.length || 0}</strong>
                  </li>
                  <li>
                    <span>Studies</span>
                    <strong>{studyCards.length}</strong>
                  </li>
                  <li>
                    <span>Open comments</span>
                    <strong>{openCommentCount}</strong>
                  </li>
                  <li>
                    <span>Unread mail</span>
                    <strong>{unreadCount}</strong>
                  </li>
                </ul>
              </section>
            </aside>
          </div>
        </>
      )}

      {userData?.StaffId ? (
        <CtmsWorkspaceModal
          open={myProfileModalOpen}
          title="My Profile"
          titleId="sponsor-cro-dashboard-my-profile-title"
          onClose={() => setMyProfileModalOpen(false)}
          className="cro-profile-workspace-modal"
          dialogClassName="ctms-workspace-modal__dialog--full"
          contentClassName="cro-profile-workspace-modal__content"
        >
          {myProfileModalOpen ? (
            <UserProfile
              key={String(userData.StaffId)}
              embedded
              embeddedStaffId={String(userData.StaffId)}
              onClose={() => setMyProfileModalOpen(false)}
            />
          ) : null}
        </CtmsWorkspaceModal>
      ) : null}
    </div>
  );
}

export default SponsorCroDashboard;
